import { useCallback, useMemo } from "react";

import { Flex, IconButton } from "@chakra-ui/react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { CommonListType } from "__graphql__/types";
import { CollaborativeInboundUpdateState } from "analytics/events";
import { routes } from "config/routes";
import { SharedListClaimers } from "flows/Inbound/components/SharedListClaimers";
import { useInboundEventTracker } from "flows/Inbound/hooks/useInboundEventTracker/useInboundEventTracker";
import { InboundingType } from "flows/Inbound/stores/inboundStore/types";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { AscDscIcon } from "ui/AscDscIcon/AscDscIcon";
import { MagnifyIcon } from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";

export function InboundDroppingListHeader() {
  const navigate = useNavigate();
  const { sendInboundStateUpdate } = useInboundEventTracker();

  const {
    droppingListId,
    droppingListName,
    deliverySSCC,
    deliveryType,
    creatorId,
    orderId,
    droppingListShelfSortOrder,
    claimedProductsStockUpdates,
    toggleDroppingListShelfSortOrder,
    setInboundUIState,
    droppingListType,
  } = useInboundStore(
    useShallow((state) => ({
      droppingListId: state.droppingListId,
      droppingListName: state.droppingListName || "",
      deliverySSCC: state.deliverySSCC,
      deliveryType: state.deliveryType,
      creatorId: state.creatorId,
      orderId: state.orderId,
      claimedProductsStockUpdates: state.claimedProductsStockUpdates,
      droppingListShelfSortOrder: state.droppingListShelfSortOrder,
      toggleDroppingListShelfSortOrder: state.toggleDroppingListShelfSortOrder,
      setInboundUIState: state.setInboundUIState,
      droppingListType: state.droppingListType,
    })),
  );

  const handleSearchFlow = useCallback(() => {
    navigate(routes.inbound.droppingManualSearch);
  }, [navigate]);

  const isDroppingComplete = useMemo(
    () =>
      Object.values(claimedProductsStockUpdates).every(
        (stockUpdateState) => !!stockUpdateState.stockUpdated,
      ),
    [claimedProductsStockUpdates],
  );
  const isSearchFlowActive = useMemo(
    () => droppingListType !== CommonListType.list_type_cancelled_order,
    [droppingListType],
  );
  const isCancelledOrder = useMemo(
    () => droppingListType === CommonListType.list_type_cancelled_order,
    [droppingListType],
  );

  const onClickGoBack = useCallback(() => {
    if (isDroppingComplete) {
      sendInboundStateUpdate({
        state: CollaborativeInboundUpdateState.DroppingListFinished,
        droppingListId,
        sscc: deliverySSCC,
        inboundingType: isCancelledOrder ? InboundingType.ReinboundedOrder : deliveryType,
        creatorId,
        orderId,
      });
      navigate(routes.inbound.root);
    }
    setInboundUIState({ isUnclaimListConfirmationModalVisible: true });
  }, [
    creatorId,
    deliverySSCC,
    deliveryType,
    droppingListId,
    isDroppingComplete,
    navigate,
    orderId,
    sendInboundStateUpdate,
    setInboundUIState,
    isCancelledOrder,
  ]);

  return (
    <NavigationHeader
      onClickGoBack={onClickGoBack}
      title={droppingListName}
      subtitle={<SharedListClaimers />}
      rightElement={
        <Flex align="center" gap="s200">
          {isSearchFlowActive && (
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Change sort order"
              icon={<MagnifyIcon color="grey.600" h="24px" w="24px" />}
              onClick={handleSearchFlow}
            />
          )}
          <IconButton
            size="xs"
            variant="ghost"
            aria-label="Change sort order"
            icon={<AscDscIcon height="24px" width="24px" sortState={droppingListShelfSortOrder} />}
            onClick={toggleDroppingListShelfSortOrder}
          />
        </Flex>
      }
    />
  );
}
